import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import {
  CheckCircle2,
  FolderKanban,
  FileText,
  Table2,
  Plus,
  Search,
  Calendar,
  Clock,
  Eye,
  Download,
  Upload,
  Edit,
  Trash2,
  ExternalLink,
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Datos de ejemplo - Tareas Completadas
const completedTasks = [
  { id: '1', titulo: 'Rediseño landing page Tech Solutions', proyecto: 'Tech Solutions', completado: '2026-01-20', responsable: 'Ana García', tiempo: '8h' },
  { id: '2', titulo: 'Configuración Meta Ads StartupX', proyecto: 'StartupX', completado: '2026-01-19', responsable: 'Luis Rodríguez', tiempo: '4h' },
  { id: '3', titulo: 'Auditoría SEO GlobalCorp', proyecto: 'GlobalCorp', completado: '2026-01-18', responsable: 'María López', tiempo: '6h' },
  { id: '4', titulo: 'Creación de copies para email marketing', proyecto: 'Fashion Brand', completado: '2026-01-17', responsable: 'Ana García', tiempo: '3h' },
  { id: '5', titulo: 'Análisis de competencia sector fitness', proyecto: 'FitnessPro', completado: '2026-01-16', responsable: 'Carlos Méndez', tiempo: '5h' },
];

// Datos de ejemplo - Proyectos
const projects = [
  { id: '1', nombre: 'Tech Solutions Inc.', estado: 'activo', progreso: 75, inicio: '2025-06-15', tipo: 'Marketing Digital', tareas: 24, completadas: 18 },
  { id: '2', nombre: 'StartupX', estado: 'activo', progreso: 45, inicio: '2025-09-01', tipo: 'Redes Sociales', tareas: 16, completadas: 7 },
  { id: '3', nombre: 'GlobalCorp', estado: 'pausado', progreso: 60, inicio: '2025-03-10', tipo: 'Consultoría', tareas: 20, completadas: 12 },
  { id: '4', nombre: 'Fashion Brand', estado: 'activo', progreso: 30, inicio: '2026-01-05', tipo: 'E-commerce', tareas: 32, completadas: 10 },
];

// Datos de ejemplo - Documentación de Procesos
const processDocs = [
  { id: '1', nombre: 'SOP - Onboarding Clientes', categoria: 'Onboarding', version: '2.1', actualizado: '2026-01-15', autor: 'Carlos Méndez' },
  { id: '2', nombre: 'Checklist - Lanzamiento Campaña Ads', categoria: 'Ads', version: '1.5', actualizado: '2026-01-10', autor: 'Ana García' },
  { id: '3', nombre: 'Guía - Creación de Contenido', categoria: 'Contenido', version: '3.0', actualizado: '2026-01-08', autor: 'Luis Rodríguez' },
  { id: '4', nombre: 'Proceso - Cierre de Venta', categoria: 'Ventas', version: '1.2', actualizado: '2026-01-05', autor: 'María López' },
  { id: '5', nombre: 'Manual - Uso de Herramientas', categoria: 'General', version: '2.0', actualizado: '2025-12-20', autor: 'Carlos Méndez' },
];

// Datos de ejemplo - Excel Karmele
const excelData = [
  { id: '1', nombre: 'Control de Proyectos 2026', tipo: 'Proyectos', actualizado: '2026-01-22', estado: 'activo' },
  { id: '2', nombre: 'Seguimiento de Clientes', tipo: 'CRM', actualizado: '2026-01-21', estado: 'activo' },
  { id: '3', nombre: 'Control de Horas Equipo', tipo: 'RRHH', actualizado: '2026-01-20', estado: 'activo' },
  { id: '4', nombre: 'Presupuestos Activos', tipo: 'Finanzas', actualizado: '2026-01-19', estado: 'activo' },
  { id: '5', nombre: 'Calendario Editorial', tipo: 'Contenido', actualizado: '2026-01-18', estado: 'activo' },
];

const estadoConfig = {
  activo: { label: 'Activo', color: 'bg-emerald-100 text-emerald-700' },
  pausado: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  completado: { label: 'Completado', color: 'bg-blue-100 text-blue-700' },
};

export function OperationsPage() {
  const [activeTab, setActiveTab] = useState('tareas');
  const [searchQuery, setSearchQuery] = useState('');

  const stats = {
    tareasCompletadas: completedTasks.length,
    proyectosActivos: projects.filter(p => p.estado === 'activo').length,
    documentos: processDocs.length,
    excels: excelData.length,
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
      data-testid="operations-page"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Operaciones</h1>
          <p className="text-muted-foreground mt-1">Gestión de tareas, proyectos y documentación</p>
        </div>
      </motion.div>

      {/* Stats */}
      <motion.div variants={itemVariants} className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Tareas Completadas</p>
                <p className="text-2xl font-bold">{stats.tareasCompletadas}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Proyectos Activos</p>
                <p className="text-2xl font-bold">{stats.proyectosActivos}</p>
              </div>
              <FolderKanban className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Documentos</p>
                <p className="text-2xl font-bold">{stats.documentos}</p>
              </div>
              <FileText className="w-8 h-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Excel Karmele</p>
                <p className="text-2xl font-bold">{stats.excels}</p>
              </div>
              <Table2 className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="tareas">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Tareas
          </TabsTrigger>
          <TabsTrigger value="proyectos">
            <FolderKanban className="w-4 h-4 mr-2" />
            Proyectos
          </TabsTrigger>
          <TabsTrigger value="documentacion">
            <FileText className="w-4 h-4 mr-2" />
            Documentación
          </TabsTrigger>
          <TabsTrigger value="excel">
            <Table2 className="w-4 h-4 mr-2" />
            Excel Karmele
          </TabsTrigger>
        </TabsList>

        {/* Tareas Completadas Tab */}
        <TabsContent value="tareas" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar tareas completadas..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>Tareas Completadas</CardTitle>
                <CardDescription>Historial de tareas finalizadas por el equipo</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium">Tarea</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Proyecto</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Responsable</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Tiempo</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Completado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedTasks.map((tarea) => (
                      <tr key={tarea.id} className="border-b hover:bg-muted/20">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span className="font-medium">{tarea.titulo}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{tarea.proyecto}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">{tarea.responsable}</td>
                        <td className="py-3 px-4 text-sm">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {tarea.tiempo}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {new Date(tarea.completado).toLocaleDateString('es-ES')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Proyectos Tab */}
        <TabsContent value="proyectos" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Proyectos Activos</h2>
              <Button className="gradient-primary text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Proyecto
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.map((proyecto) => (
                <Card key={proyecto.id} className="card-hover">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{proyecto.nombre}</CardTitle>
                        <CardDescription>{proyecto.tipo}</CardDescription>
                      </div>
                      <Badge className={estadoConfig[proyecto.estado]?.color}>
                        {estadoConfig[proyecto.estado]?.label}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground">Progreso</span>
                        <span className="text-sm font-medium">{proyecto.progreso}%</span>
                      </div>
                      <Progress value={proyecto.progreso} className="h-2" />
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Inicio: {new Date(proyecto.inicio).toLocaleDateString('es-ES')}
                      </span>
                      <span>
                        {proyecto.completadas}/{proyecto.tareas} tareas
                      </span>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver Detalle
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </TabsContent>

        {/* Documentación Tab */}
        <TabsContent value="documentacion" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Documentación de Procesos</h2>
              <Button className="gradient-primary text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Documento
              </Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium">Documento</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Categoría</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Versión</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Autor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium">Actualizado</th>
                      <th className="text-left py-3 px-4 text-sm font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {processDocs.map((doc) => (
                      <tr key={doc.id} className="border-b hover:bg-muted/20">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">{doc.nombre}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{doc.categoria}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">v{doc.version}</td>
                        <td className="py-3 px-4 text-sm">{doc.autor}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {new Date(doc.actualizado).toLocaleDateString('es-ES')}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>

        {/* Excel Karmele Tab */}
        <TabsContent value="excel" className="space-y-6">
          <motion.div variants={itemVariants}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">Excel Karmele</h2>
                <p className="text-sm text-muted-foreground">Documentos Excel de gestión</p>
              </div>
              <Button className="gradient-primary text-white">
                <Upload className="w-4 h-4 mr-2" />
                Subir Excel
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {excelData.map((excel) => (
                <Card key={excel.id} className="card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <Table2 className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{excel.nombre}</h3>
                        <p className="text-sm text-muted-foreground">{excel.tipo}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Actualizado: {new Date(excel.actualizado).toLocaleDateString('es-ES')}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" size="sm" className="flex-1">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Abrir
                      </Button>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </motion.div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
